/**
 * Chat API Service - Handles NDJSON streaming from /chat endpoint
 * Mirrors the streaming protocol from ui_app.py
 */

import { ChatStreamEvent } from '../types/chat.types';
import { CachedLocation, getUserLocationRecord } from '@src/v3/utils/location';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001';
const API_KEY = process.env.NEXT_PUBLIC_API_KEY || '';
interface ChatRequest {
  message: string;
  session_id: string;
  history: Array<{ role: 'user' | 'system'; content: string }>;
  user_location?: CachedLocation;
  is_registered?: boolean;
}

/**
 * Sanitize JSON string to handle Infinity/NaN
 */
function sanitizeJson(jsonStr: string): string {
  return jsonStr
    .replace(/:\s*Infinity/g, ': null')
    .replace(/:\s*-Infinity/g, ': null')
    .replace(/NaN/g, 'null');
}

/**
 * Stream chat API with NDJSON protocol
 * Yields parsed JSON objects as they arrive
 */
export async function* streamChat(
  message: string,
  sessionId: string,
  history: Array<{ role: 'user' | 'system'; content: string }>
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  const request: ChatRequest = {
    message,
    session_id: sessionId,
    history,
    is_registered: true,
  };

  try {
    let locationRecord: CachedLocation | null = null;
    try {
      locationRecord = await getUserLocationRecord();
    } catch (locationError) {
      console.warn(
        '[chatApiService] Could not determine user location:',
        locationError
      );
    }

    if (locationRecord) {
      request.user_location = locationRecord;
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      const errorEvent: ChatStreamEvent & { status?: number } = {
        type: 'error',
        content: errorText || 'Request failed',
        status: response.status,
      } as ChatStreamEvent & { status?: number };
      yield errorEvent;
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      yield {
        type: 'error',
        content: 'Response body not readable',
      };
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Split by newlines and process complete lines
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        try {
          const safeLine = sanitizeJson(trimmedLine);
          const data = JSON.parse(safeLine);

          // Validate and yield the event
          yield data as ChatStreamEvent;
        } catch (parseError) {
          console.warn(
            '[chatApiService] Skipping malformed JSON line:',
            trimmedLine
          );
          console.warn('[chatApiService] Parse error:', parseError);
          // Continue processing, don't abort
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim()) {
      try {
        const safeLine = sanitizeJson(buffer.trim());
        const data = JSON.parse(safeLine);
        yield data as ChatStreamEvent;
      } catch (parseError) {
        console.warn('[chatApiService] Could not parse final buffer:', buffer);
      }
    }
  } catch (error) {
    const errorEvent: ChatStreamEvent & { status?: number } = {
      type: 'error',
      content: error instanceof Error ? error.message : 'Unknown error',
      status: 0,
    } as ChatStreamEvent & { status?: number };
    yield errorEvent;
  }
}

/**
 * Helper: Generate a unique session ID
 */
export function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
